import { FC, useState } from 'react';

import { XIcon } from '@heroicons/react/outline';
import { GiftChoiceUnitData } from '@shared/api/giftChoiceUnit/giftChoiceUnit.interface';
import {
  AdditionalOption,
  CouponDetail,
  DeliveryDetail,
  GiftItemInfoType,
} from '@shared/api/giftItemInfo/giftItemInfo.interface';
import { Image } from '@shared/api/media/media.interface';
import { fileBucketUrl } from '@shared/utils/fileUtils';
import { formatPrice } from '@shared/utils/formatPrice';
import { giftDiscountRate } from '@shared/utils/giftDiscountRate.util';

/**
 * 상품 정보 축약 모달 (읽기 전용).
 *
 * 전화 응대 직원(전화주문 관리 > 상품 목록)과 답례품 배송관리자(판매상품관리)가
 * "고객 상세페이지에 무엇이 적혀 있는지"를 같은 화면으로 확인하기 위한 공용 모달이다.
 * 데이터 출처는 콘솔마다 다르므로(상조별 GiftItem / 원상품 GiftItemInfo)
 * 호출부가 아래 GiftItemPreviewSource 모양으로 정규화해서 넘긴다.
 */

/** 썸네일 — 목록/카드와 같은 관습 */
export const giftImageThumbUrl = (image: unknown): string => {
  if (image && typeof image === 'object' && 'url' in image) {
    return fileBucketUrl((image as Image).url, 'w400');
  }
  return '';
};

/** 크게 볼 때는 원본 — 확대해도 흐려지지 않게 */
export const giftImageRawUrl = (image: unknown): string => {
  if (image && typeof image === 'object' && 'url' in image) {
    return fileBucketUrl((image as Image).url, 'raw');
  }
  return '';
};

// 할인율 계산은 공용 유틸 하나로 (소수점 올림) — 기존 import 경로 유지를 위해 재수출
export { giftDiscountRate };

export interface GiftItemPreviewSource {
  name: string;
  image?: unknown;
  type: GiftItemInfoType;
  isSelectable?: boolean;
  /** false면 "판매중지" 배지 */
  isEnabled?: boolean;
  priceRegular: number;
  priceRetail: number;
  detailInfo?: string;
  tags?: string[];
  minOrderQuantity?: number;
  orderUnit?: number;
  couponDetail?: CouponDetail;
  deliveryDetail?: DeliveryDetail;
  allowIndividualDelivery?: boolean;
  useThanksPrint?: boolean;
  additionalOptions?: AdditionalOption<Image>[];
  choiceUnits?: GiftChoiceUnitData[];
  /** 상세 이미지 — 고객 상세페이지 순서(카테고리 맥락설명 → 상품)로 합쳐서 전달 */
  detailImages?: Image[];
  /** 콘솔별 추가 정보 행 (예: 도매가·판매중인 상조) */
  extraRows?: { label: string; value: string }[];
}

export const GiftItemPreviewModal: FC<{
  source: GiftItemPreviewSource;
  /** 선택지 등 추가 데이터를 아직 불러오는 중인지 */
  loading?: boolean;
  onClose: () => void;
}> = function GiftItemPreviewModal({ source, loading, onClose }) {
  const [showDetailImages, setShowDetailImages] = useState(false);
  // 넓게 보기: 상세 이미지·선택 상품을 크게 확인할 때
  const [wide, setWide] = useState(false);
  // 이미지 크게 보기 (원본) — 누른 이미지 하나를 화면 가득
  const [viewerUrl, setViewerUrl] = useState<string | null>(null);

  const isCoupon = source.type === GiftItemInfoType.Coupon;
  const discount = giftDiscountRate(source.priceRegular, source.priceRetail);
  const minQuantity = source.minOrderQuantity ?? 1;
  const tags = source.tags ?? [];
  const options = source.additionalOptions ?? [];
  const choiceUnits = (source.choiceUnits ?? []).filter(
    (unit) => unit.enabled !== false,
  );
  const detailImageList = source.detailImages ?? [];
  const shippingFee = source.deliveryDetail?.shippingFee;
  const thumbUrl = giftImageThumbUrl(source.image);

  // 핵심 정보를 라벨-값 표로 축약 (상세페이지의 마케팅 요소는 제외)
  const infoRows: { label: string; value: string }[] = [];
  infoRows.push({
    label: '주문 수량',
    value: `${minQuantity > 1 ? `${minQuantity}개부터` : '1개부터'}${
      source.orderUnit != null && source.orderUnit > 1 ? ` · ${source.orderUnit}개씩` : ''
    }`,
  });
  if (minQuantity > 1) {
    infoRows.push({
      label: '최소 주문 금액',
      value: `${formatPrice(source.priceRetail * minQuantity)}원 (${minQuantity}개)`,
    });
  }
  if (isCoupon && source.couponDetail?.validPeriod) {
    infoRows.push({ label: '유효기간', value: source.couponDetail.validPeriod });
  }
  if (isCoupon && source.couponDetail?.placeOfExchange) {
    infoRows.push({ label: '교환처', value: source.couponDetail.placeOfExchange });
  }
  if (!isCoupon) {
    infoRows.push({
      label: '배송비',
      value:
        shippingFee != null && shippingFee > 0
          ? `${formatPrice(shippingFee)}원${
              source.deliveryDetail?.freeShippingMinQuantity
                ? ` (${source.deliveryDetail.freeShippingMinQuantity}개 이상 무료)`
                : ''
            }`
          : '없음',
    });
    if (source.deliveryDetail?.shippingInfo) {
      infoRows.push({ label: '배송 안내', value: source.deliveryDetail.shippingInfo });
    }
    if (source.allowIndividualDelivery === false) {
      infoRows.push({ label: '배송 방식', value: '한 주소로만 배송 (개별 배송 불가)' });
    }
    if (source.useThanksPrint === true) {
      infoRows.push({ label: '감사카드', value: '인쇄 가능' });
    }
  }
  infoRows.push(...(source.extraRows ?? []));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={onClose}
    >
      <div
        className={`max-h-[92vh] w-full overflow-y-auto rounded-xl bg-white shadow-xl ${
          wide ? 'max-w-6xl' : 'max-w-2xl'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* 헤더: 이미지 + 이름/가격 */}
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b border-gray-100 bg-white p-4">
          <div className="flex gap-3">
            {thumbUrl ? (
              <img
                src={thumbUrl}
                alt={source.name}
                onClick={() => setViewerUrl(giftImageRawUrl(source.image))}
                className="h-20 w-20 flex-shrink-0 cursor-zoom-in rounded-lg object-cover"
              />
            ) : (
              <div className="h-20 w-20 flex-shrink-0 rounded-lg bg-gray-100" />
            )}
            <div>
              <div className="flex flex-wrap gap-1">
                <span
                  className={`rounded px-1.5 py-0.5 text-[11px] font-bold text-white ${
                    isCoupon ? 'bg-purple-500' : 'bg-blue-500'
                  }`}
                >
                  {isCoupon ? '쿠폰' : '배송형 상품'}
                </span>
                {source.isSelectable && (
                  <span className="rounded bg-indigo-500 px-1.5 py-0.5 text-[11px] font-bold text-white">
                    선택형
                  </span>
                )}
                {source.isEnabled === false && (
                  <span className="rounded bg-gray-700 px-1.5 py-0.5 text-[11px] font-bold text-white">
                    판매중지
                  </span>
                )}
              </div>
              <h3 className="mt-1 break-keep text-base font-bold leading-tight">
                {source.name}
              </h3>
              <p className="mt-1 flex items-baseline gap-1">
                {discount > 0 && (
                  <span className="text-sm font-extrabold text-red-500">{discount}%</span>
                )}
                <span className="text-lg font-extrabold">
                  {formatPrice(source.priceRetail)}원
                </span>
                {discount > 0 && (
                  <span className="text-xs text-gray-400 line-through">
                    {formatPrice(source.priceRegular)}원
                  </span>
                )}
              </p>
            </div>
          </div>
          <div className="flex flex-shrink-0 items-center gap-1">
            <button
              onClick={() => setWide((prev) => !prev)}
              className="hidden rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-600 hover:bg-gray-50 lg:block"
            >
              {wide ? '좁게 보기' : '넓게 보기'}
            </button>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-gray-400 hover:bg-gray-100"
            >
              <XIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* 핵심 정보 표 */}
          <table className="w-full text-sm">
            <tbody className="divide-y divide-gray-100">
              {infoRows.map((row) => (
                <tr key={row.label}>
                  <td className="w-24 py-1.5 pr-2 align-top text-xs text-gray-400">
                    {row.label}
                  </td>
                  <td className="break-keep py-1.5 text-gray-800">{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {tags.map((tag, i) => (
                <span
                  key={`${tag}-${i}`}
                  className="rounded-full bg-teal-50 px-2 py-0.5 text-xs font-medium text-teal-700"
                >
                  #{tag}
                </span>
              ))}
            </div>
          )}

          {/* 선택형: 선택 가능한 상품 */}
          {source.isSelectable && (
            <div>
              <h4 className="mb-2 text-sm font-bold text-gray-900">
                선택 가능한 상품
                {choiceUnits.length > 0 && ` (${choiceUnits.length}종)`}
                <span className="ml-1 text-xs font-normal text-gray-400">
                  받는 분이 하나를 골라요
                </span>
              </h4>
              {loading ? (
                <div className="py-4 text-center text-xs text-gray-400">불러오는 중…</div>
              ) : choiceUnits.length > 0 ? (
                <div
                  className={`grid gap-2 ${
                    wide ? 'grid-cols-2 xl:grid-cols-3' : 'grid-cols-1 sm:grid-cols-2'
                  }`}
                >
                  {choiceUnits.map((unit) => {
                    //* 브랜드(예: 스타벅스)는 상담에서 바로 쓰는 정보 — 이름 위에 표시
                    const unitBrand =
                      typeof unit.giftItemBrand === 'object'
                        ? unit.giftItemBrand?.name
                        : undefined;
                    return (
                      <div
                        key={unit._id}
                        className="flex items-start gap-2 rounded-lg border border-gray-100 p-2"
                      >
                        {giftImageThumbUrl(unit.image) ? (
                          <img
                            src={giftImageThumbUrl(unit.image)}
                            alt={unit.name}
                            onClick={() => setViewerUrl(giftImageRawUrl(unit.image))}
                            className="h-14 w-14 flex-shrink-0 cursor-zoom-in rounded-md object-cover"
                          />
                        ) : (
                          <div className="h-14 w-14 flex-shrink-0 rounded-md bg-gray-100" />
                        )}
                        <div className="min-w-0 flex-1">
                          {unitBrand && (
                            <p className="text-[11px] font-semibold text-indigo-600">
                              {unitBrand}
                            </p>
                          )}
                          <p className="break-keep text-xs font-medium leading-snug text-gray-900">
                            {unit.name}
                          </p>
                          {unit.description && (
                            <p className="break-keep text-[11px] leading-snug text-gray-500">
                              {unit.description}
                            </p>
                          )}
                          {unit.priceRegular != null && unit.priceRegular > 0 && (
                            <p className="text-[11px] text-gray-400">
                              정가 {formatPrice(unit.priceRegular)}원
                            </p>
                          )}
                          {unit.couponDetail?.placeOfExchange && (
                            <p className="break-keep text-[11px] leading-snug text-gray-400">
                              {unit.couponDetail.placeOfExchange}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-gray-400">
                  선택할 수 있는 상품 정보를 불러오지 못했습니다.
                </p>
              )}
            </div>
          )}

          {/* 추가옵션 */}
          {options.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-sm font-bold text-gray-900">추가옵션</h4>
              <div className="space-y-1 text-xs text-gray-700">
                {options.map((option) => (
                  <p key={option.name}>
                    <span className="font-medium">{option.name}</span>:{' '}
                    {(option.choices ?? [])
                      .filter((choice) => choice.enabled !== false)
                      .map(
                        (choice) =>
                          `${choice.name}${
                            choice.priceDelta
                              ? ` (+${formatPrice(choice.priceDelta)}원)`
                              : ''
                          }`,
                      )
                      .join(', ')}
                  </p>
                ))}
              </div>
            </div>
          )}

          {/* 상품 설명 */}
          {source.detailInfo && (
            <div>
              <h4 className="mb-1.5 text-sm font-bold text-gray-900">상품 설명</h4>
              <p className="whitespace-pre-line break-keep text-xs leading-relaxed text-gray-600">
                {source.detailInfo}
              </p>
            </div>
          )}

          {/* 상세 이미지 (펼치기) — 고객 상세페이지 하단과 같은 이미지 */}
          {detailImageList.length > 0 && (
            <div>
              <button
                onClick={() => setShowDetailImages((prev) => !prev)}
                className="w-full rounded-md border border-gray-300 bg-white py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                {showDetailImages
                  ? '상세 이미지 접기'
                  : `상세 이미지 보기 (${detailImageList.length}장)`}
              </button>
              {showDetailImages && (
                <div className="mt-2">
                  <p className="mb-1 text-[11px] text-gray-400">
                    이미지를 누르면 화면 가득 크게 볼 수 있어요
                  </p>
                  {detailImageList.map((img, idx) => (
                    <img
                      key={`detail-${idx}`}
                      src={fileBucketUrl(img.url, 'raw')}
                      alt={`상세이미지 ${idx + 1}`}
                      onClick={() => setViewerUrl(fileBucketUrl(img.url, 'raw'))}
                      className="w-full cursor-zoom-in"
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* 이미지 크게 보기 */}
      {viewerUrl && (
        <div
          className="fixed inset-0 z-[60] overflow-auto bg-black/90 p-4"
          onClick={(e) => {
            e.stopPropagation();
            setViewerUrl(null);
          }}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              setViewerUrl(null);
            }}
            className="fixed right-4 top-4 rounded-full bg-white/90 p-1.5 text-gray-700 shadow"
          >
            <XIcon className="h-6 w-6" />
          </button>
          <img
            src={viewerUrl}
            alt="크게 보기"
            className="mx-auto block max-w-full cursor-zoom-out"
          />
        </div>
      )}
    </div>
  );
};
